// ===========================================
// Mobile Bottom Navigation Component
// ===========================================
// Fixed bottom navigation bar for mobile devices

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import {
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
  Slide,
  useTheme,
} from '@mui/material';
import {
  Dashboard,
  CalendarMonth,
  Email,
  Person,
  Handshake,
  EventAvailable,
  NoteAlt,
  Folder,
  MoreHoriz,
} from '@mui/icons-material';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import { NAV_PERMISSION_MAP } from './filterAdminNavigation';

// ===========================================
// Types
// ===========================================

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  badgeColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
}

interface BottomNavigationProps {
  /** User role to determine navigation items */
  role: 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';
  /** Number of pending meet & greets (admin only) */
  pendingMeetAndGreets?: number;
  /** Number of unread messages */
  unreadMessages?: number;
  /** Whether user is in student view mode (parent only) */
  isStudentView?: boolean;
  /** Callback when "More" is clicked to open drawer */
  onMoreClick?: () => void;
  /**
   * The staff view (roles 1b §5.15): the office items are kept only where this grants their
   * resource, as in the sidebar (`NAV_PERMISSION_MAP`). Absent = every item for the role.
   */
  canAny?: (resource: string) => boolean;
}

// ===========================================
// Navigation Configurations
// ===========================================

const getAdminNavItems = (
  pendingMeetAndGreets: number,
  unreadMessages: number
): NavItem[] => [
  { label: 'Dashboard', path: '/admin', icon: <Dashboard /> },
  { label: 'Calendar', path: '/admin/calendar', icon: <CalendarMonth /> },
  {
    label: 'Messages',
    path: '/admin/messages',
    icon: <Email />,
    badge: unreadMessages,
    badgeColor: 'error',
  },
  {
    label: 'Meet & Greet',
    path: '/admin/meet-and-greet',
    icon: <Handshake />,
    badge: pendingMeetAndGreets,
    badgeColor: 'warning',
  },
  { label: 'More', path: '#more', icon: <MoreHoriz /> },
];

const getTeacherNavItems = (unreadMessages: number): NavItem[] => [
  { label: 'Dashboard', path: '/teacher', icon: <Dashboard /> },
  { label: 'Tasks', path: '/teacher/teacher-tasks', icon: <NoteAlt /> },
  { label: 'Calendar', path: '/teacher/calendar', icon: <CalendarMonth /> },
  {
    label: 'Messages',
    path: '/teacher/messages',
    icon: <Email />,
    badge: unreadMessages,
    badgeColor: 'error',
  },
  { label: 'Files', path: '/teacher/files', icon: <Folder /> },
];

const getParentNavItems = (unreadMessages: number): NavItem[] => [
  { label: 'Dashboard', path: '/parent', icon: <Dashboard /> },
  { label: 'Bookings', path: '/parent/bookings', icon: <EventAvailable /> },
  {
    label: 'Messages',
    path: '/parent/messages',
    icon: <Email />,
    badge: unreadMessages,
    badgeColor: 'error',
  },
  { label: 'Resources', path: '/parent/resources', icon: <Folder /> },
  { label: 'More', path: '#more', icon: <MoreHoriz /> },
];

const getStudentNavItems = (unreadMessages: number): NavItem[] => [
  { label: 'Dashboard', path: '/student', icon: <Dashboard /> },
  {
    label: 'Messages',
    path: '/student/messages',
    icon: <Email />,
    badge: unreadMessages,
    badgeColor: 'error',
  },
  { label: 'Profile', path: '/student/account', icon: <Person /> },
];

const getStudentViewNavItems = (): NavItem[] => [
  { label: 'Dashboard', path: '/parent', icon: <Dashboard /> },
];

// ===========================================
// Component
// ===========================================

export function BottomNavigation({
  role,
  pendingMeetAndGreets = 0,
  unreadMessages = 0,
  isStudentView = false,
  onMoreClick,
  canAny,
}: BottomNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isVisible } = useScrollDirection({ threshold: 15 });
  const schoolSlug = extractSchoolSlugFromPath(location.pathname);

  // Get navigation items based on role
  const navItems = useMemo((): NavItem[] => {
    if (isStudentView) {
      return getStudentViewNavItems();
    }

    switch (role) {
      case 'ADMIN': {
        const items = getAdminNavItems(pendingMeetAndGreets, unreadMessages);
        if (!canAny) return items;
        return items.filter((item) => {
          if (item.path === '#more') return true;
          const resource = NAV_PERMISSION_MAP[item.label];
          return resource ? canAny(resource) : false;
        });
      }
      case 'TEACHER':
        return getTeacherNavItems(unreadMessages);
      case 'PARENT':
        return getParentNavItems(unreadMessages);
      case 'STUDENT':
        return getStudentNavItems(unreadMessages);
      default:
        return [];
    }
  }, [role, pendingMeetAndGreets, unreadMessages, isStudentView, canAny]);

  // Find active navigation index
  const activeIndex = useMemo(() => {
    const slugPrefix = schoolSlug ? `/${schoolSlug}` : '';

    // First check for exact match (with or without slug prefix)
    const exactMatch = navItems.findIndex(
      (item) => item.path === location.pathname || `${slugPrefix}${item.path}` === location.pathname
    );
    if (exactMatch !== -1) return exactMatch;

    // Then check for prefix match (for nested routes)
    const prefixMatch = navItems.findIndex(
      (item) =>
        item.path !== '#more' &&
        item.path !== '/' &&
        (location.pathname.startsWith(item.path) ||
          location.pathname.startsWith(`${slugPrefix}${item.path}`))
    );
    if (prefixMatch !== -1) return prefixMatch;

    // Default to first item if on a nested page that doesn't match
    return 0;
  }, [navItems, location.pathname, schoolSlug]);

  const [value, setValue] = useState(activeIndex);

  // Update value when route changes
  useEffect(() => {
    setValue(activeIndex);
  }, [activeIndex]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const item = navItems[newValue];
    if (!item) return;

    if (item.path === '#more') {
      onMoreClick?.();
      return;
    }

    setValue(newValue);
    const fullPath = schoolSlug ? `/${schoolSlug}${item.path}` : item.path;
    navigate(fullPath);
  };

  // Don't render if no nav items or only one item
  if (navItems.length <= 1) {
    return null;
  }

  return (
    <Slide appear={false} direction="up" in={isVisible}>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          borderTop: 1,
          borderColor: 'divider',
          // Add safe area padding for iOS
          pb: 'env(safe-area-inset-bottom)',
        }}
        elevation={8}
      >
        <MuiBottomNavigation
          value={value}
          onChange={handleChange}
          showLabels
          sx={{
            height: 64,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              py: 1,
              px: 0.5,
              '&.Mui-selected': {
                color: 'primary.main',
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.65rem',
              '&.Mui-selected': {
                fontSize: '0.7rem',
              },
            },
          }}
        >
          {navItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={
                item.badge && item.badge > 0 ? (
                  <Badge
                    badgeContent={item.badge}
                    color={item.badgeColor || 'error'}
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        minWidth: 16,
                        height: 16,
                        padding: '0 4px',
                      },
                    }}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )
              }
              sx={{
                // Ensure touch target is at least 44x44
                minHeight: 44,
              }}
            />
          ))}
        </MuiBottomNavigation>
      </Paper>
    </Slide>
  );
}

export default BottomNavigation;
